import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { Menu as MenuIcon, Sparkles, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../i18n/I18nProvider";
import { type AccountUser } from "../../lib/auth";
import { Logo } from "../../components/Logo";
import { LanguageMenu } from "../../components/LanguageMenu";
import { ThemeToggle } from "../../components/ThemeToggle";
import { TabButton } from "../TabButton";
import type { TabKey } from "../tabs";
import { AccountMenu } from "./AccountMenu";

/** Where the CMS link points and what it is called - resolved by the layout. */
export type AdminLink = "editor" | "cms" | null;

/**
 * The hub's top bar: the wordmark, the client's services, and everything that
 * is about the signed-in person.
 *
 * Lifted out of `AccountLayout`, which was well past the file size guideline
 * and had the whole bar inline. The layout still decides WHAT may appear - who
 * the visitor is, which tabs they may open - and hands that down as props; this
 * file only decides where each of those things sits at each width.
 */
export function AccountHeader({
  user,
  portalBrand,
  adminLink,
  crmLink,
  active,
  portalTabs,
  menuTabs,
  showInvite,
  onGo,
  onInvite,
  onLogout,
}: {
  /**
   * The signed-in person, or null in a preview - where the screen is not about
   * the visitor at all and must not offer them a way out of their own session.
   */
  user: AccountUser | null;
  /** The operator's client portal rather than Husly's own hub. */
  portalBrand: boolean;
  adminLink: AdminLink;
  /** The staff CRM, for somebody the CRM would let in. */
  crmLink: boolean;
  active: TabKey;
  /** The client's service tabs - the header row from `lg` up. */
  portalTabs: { key: TabKey }[];
  /** The account tabs the hamburger holds under `lg`, already filtered. */
  menuTabs: { key: TabKey; icon: LucideIcon }[];
  /** The referral programme is on and this visit is entitled to see it. */
  showInvite: boolean;
  onGo: (tab: TabKey) => void;
  onInvite: () => void;
  onLogout: () => void;
}) {
  const t = useT();
  // The narrow-screen menu behind the hamburger (<lg): the account tabs, the
  // language, the theme and the CMS link. Closes on an outside click or Escape.
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menuOpen) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setMenuOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-background/70 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-5">
          {/* Both marks are sized as a front door, because that is what this
              header is on either host - the first thing a client sees, beside
              a nav of five words. It was briefly paired with the CRM's top bar
              (17px, `web_crm/src/crm/shell/TopBar.tsx`), which is the wrong
              pair: that bar is a dense operator chrome the client never opens,
              and a wordmark cut to fit it reads as an afterthought here. The
              portal mark stays a step under Husly's own only because it is
              twice as wide for its height (1982x374 against 1042x355), so at
              equal height it would crowd the nav off the row. */}
          <Logo
            variant="full"
            className={cn("w-auto text-foreground", portalBrand ? "h-6 md:h-7" : "h-8 md:h-9")}
          />
          {/* The client's services, and from `lg` up this is the ONLY place
              they are. The side menu below carries them under `lg`, where
              this row is hidden - one list at a time, never two.
              Why the split runs at `lg` rather than `sm`: five Estonian
              labels plus the wordmark plus the language and theme controls
              do not fit a 700px bar, and the last tab ends up underneath the
              language button. `lg` is also exactly where the side menu turns
              from a row into a column, so the two switch together. */}
          <nav className="hidden items-center gap-0.5 lg:flex">
            {portalTabs.map(({ key }) => (
              <button
                key={key}
                onClick={() => onGo(key)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  active === key ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                )}
              >
                {t(`tabs.${key}`)}
              </button>
            ))}
          </nav>
        </div>
        <div className="flex items-center gap-2">
          {/* The account dropdown, at every width - the avatar alone under `sm`.
              It carries the person's name, the sibling apps, their own screens,
              the language and the theme, which is why none of those are loose in
              this row any more. A preview has no signed-in person to open it
              for, so there the two switches stay where they were. */}
          {user ? (
            <AccountMenu
              user={user}
              portalBrand={portalBrand}
              adminLink={adminLink}
              crmLink={crmLink}
              openTabs={menuTabs.map((tab) => tab.key)}
              onGo={onGo}
              onLogout={onLogout}
            />
          ) : (
            <>
              <LanguageMenu />
              <ThemeToggle />
            </>
          )}

        {/* Narrow (<lg): one hamburger on the right holding the person's
            account screens, while the row under the banner holds their
            services. The split is what keeps a phone from showing eleven tabs
            in one scrolling strip where five of them are off-screen. */}
        <div ref={menuRef} className="relative lg:hidden">
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            aria-label={t("common.menu")}
            className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-foreground transition-colors hover:bg-accent"
          >
            {menuOpen ? <X className="size-5" /> : <MenuIcon className="size-5" />}
          </button>
          {menuOpen && (
            <div
              role="menu"
              className="absolute right-0 top-[calc(100%+8px)] z-50 max-h-[calc(100dvh-5rem)] min-w-60 overflow-y-auto rounded-xl border border-border bg-card p-2.5 shadow-lg"
            >
              {/* The account screens. They are the same buttons the side menu
                  draws as a column from `lg` up; down here that column would
                  have to share a scrolling strip with the services, so they
                  move in here instead. `pill={false}`: the sliding indicator
                  is a `layoutId`, and two live copies of one id make it jump
                  between them - the column is hidden under `lg` but still
                  mounted. */}
              {menuTabs.map(({ key, icon: Icon }) => (
                <TabButton
                  key={key}
                  tab={key}
                  Icon={Icon}
                  active={active === key}
                  label={t(`tabs.${key}`)}
                  pill={false}
                  className="w-full"
                  onClick={() => {
                    onGo(key);
                    setMenuOpen(false);
                  }}
                />
              ))}
              {showInvite && (
                <button
                  onClick={() => {
                    onInvite();
                    setMenuOpen(false);
                  }}
                  className="flex w-full shrink-0 items-center gap-2.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary/10"
                >
                  <Sparkles className="size-4" />
                  <span>{t("inviteModal.menuLabel")}</span>
                </button>
              )}
            </div>
          )}
        </div>
        </div>
      </div>
    </header>
  );
}
